import copy from "copy-to-clipboard"

import useIsDesktop from "@hooks/useIsDesktop"

import debounce from "@utils/debounce"
import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { TruncatableText } from "@particles/index"

import { IconButton, NavigationLink } from "@atoms/index"

import toast from "@molecules/Toast/Toast"

import { Table } from "@organisms"

import styles from "./LinkCell.module.scss"
import { LinkCellProps } from "./LinkCell.types"

export default function LinkCell(props: LinkCellProps) {
    const { destination, showCopyButton = true, isLoading } = props

    const isDesktop = useIsDesktop()

    if (!destination) {
        return <Table.Cells.NoValueCell />
    }

    const handleCopy = debounce(
        (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.KeyboardEvent<HTMLButtonElement>) => {
            event.stopPropagation()

            const result = copy(destination)

            if (result) {
                toast({
                    type: "normal",
                    variant: "default",
                    title: "Website link copied.",
                    iconOverride: faIcon({ name: "copy", style: "solid" }),
                })
            } else {
                toast({
                    type: "error",
                    variant: "default",
                    title: "Could not copy website link...",
                })
            }
        },
        100,
    )

    if (isLoading) {
        return <Table.Cells.SkeletonCell variant="text" />
    } else {
        return (
            <div
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => ifSpaceOrEnter(event, () => event.stopPropagation())}
                className={styles.base}
            >
                <NavigationLink
                    path={`${destination.includes("//") ? "" : "//"}${destination}`}
                    title={destination}
                    openIn="newTab"
                    className={styles.link}
                >
                    {() => <TruncatableText text={destination} tooltipOnTruncate={true} tooltipPlacement="bottom" />}
                </NavigationLink>

                {isDesktop && showCopyButton && (
                    <IconButton
                        icon={faIcon({ name: "copy", style: "regular" })}
                        onClick={handleCopy}
                        onKeyDown={handleCopy}
                        size="md"
                        variant="muted"
                    />
                )}
            </div>
        )
    }
}
